import React from "react";
import { motion } from "framer-motion";
import { TESTIMONIALS } from "@/lib/site-data";
import { Quote, Star, TrendingUp, Calendar } from "lucide-react";
import { FinalCTA } from "@/components/Sections";

export default function SuccessStoriesPage() {
  return (
    <main className="pt-32 sm:pt-36 pb-10 overflow-hidden" data-testid="success-stories-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#9db1ff]">Success stories</p>
          <h1 className="mt-4 font-heading text-5xl md:text-6xl font-bold text-white tracking-tighter leading-[1.05]">
            Real candidates. <span className="text-gradient-electric">Real US offers.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-lg leading-relaxed">
            Inside each story: where they started, the strategy we built, and the final offer.
          </p>
        </div>

        {/* Stat band */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { v: "1,240+", l: "Candidates placed" },
            { v: "$105k", l: "Average package" },
            { v: "94%", l: "Success rate" },
            { v: "180+", l: "Hiring companies" },
          ].map((s) => (
            <div key={s.l} className="glass p-6">
              <p className="font-heading text-3xl text-white">{s.v}</p>
              <p className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.article
              key={t.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 2) * 0.06 }}
              className="glass-prism p-8 md:p-10"
              data-testid={`story-${i}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2B4DFF] to-[#8A2BE2] flex items-center justify-center text-white font-semibold">
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-white font-medium">{t.name}</p>
                    <p className="text-xs text-zinc-400">{t.role} · {t.company}</p>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>

              <Quote className="w-6 h-6 text-[#5b78ff] mt-6 mb-3" />
              <p className="text-white text-base leading-relaxed">{t.review}</p>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <BeforeAfter label="Before" value={t.before} tone="muted" />
                <BeforeAfter label="During" value={t.interviews} tone="info" />
                <BeforeAfter label="Offer" value={t.package} tone="success" />
              </div>

              <div className="mt-6 pt-6 border-t border-white/[0.06] flex items-center justify-between text-xs text-zinc-500">
                <span className="inline-flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Placed in {t.weeks}</span>
                <span className="inline-flex items-center gap-2 text-emerald-300"><TrendingUp className="w-3.5 h-3.5" /> {t.above} above target</span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <FinalCTA />
    </main>
  );
}

const BeforeAfter = ({ label, value, tone }) => {
  const t =
    tone === "success"
      ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
      : tone === "info"
      ? "border-[#5b78ff]/30 bg-[#2B4DFF]/10 text-[#9db1ff]"
      : "border-white/[0.08] bg-white/[0.03] text-zinc-300";
  return (
    <div className={`rounded-xl border p-3 ${t}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-sm font-semibold mt-1">{value}</p>
    </div>
  );
};
